import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { SelfFilerService } from '@services/self-filer.service';
import { AppLayoutService } from '../../app-layout.service';
import { GenericService } from '@services/generic.service';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { IChildMenuItem, IChildMenuItemDropdown, ISidebarMenuItem } from '../../interfaces';
import { enumHeaderActionId, enumSidebarMenuItem } from '../../enums';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { IsfBatchSidebarComponent } from '@shared/isf-mutual/components';
import { AppService } from '@app/app.service';
import { EsBatchSidebarComponent } from '@shared/es-mutual/components';

declare let Cookiebot: any;

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  public openCompanies: boolean = false;
  public showMenu: string;
  public isOpenSideBarClass: string = 'sidebar-open';
  public company: CompanyModel;
  public isTablet$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  public version$: BehaviorSubject<{ version: string; timestamp: string }>;
  public isDesktop: boolean;

  public menuItemSettings: ISidebarMenuItem[] = [];

  public get isActiveSupportMode(): boolean {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT;
  }

  public get isActiveUserMode(): boolean {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.USER;
  }

  public get isAdmin(): boolean {
    return this.companiesService.isAdmin();
  }

  public get isSupportOrAdmin(): boolean {
    return this.companiesService.isSupportOrAdmin();
  }

  public get isSuperAdmin(): boolean {
    return this.companiesService.isSuperAdmin();
  }

  public get isAllowSwitchMode(): boolean {
    return this.isSupportOrAdmin && this.companiesService.isUser();
  }

  @ViewChild('sideBar', { read: ElementRef, static: false }) public sideBar: ElementRef;

  private listener: () => void;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private router: Router,
    private companiesService: CompaniesService,
    private appLayoutService: AppLayoutService,
    private renderer: Renderer2,
    private selfFilerService: SelfFilerService,
    private genericService: GenericService,
    private applicationModeService: ApplicationModeService,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
  ) {
    this.version$ = this.genericService.appVersion$;
    this.isTablet$ = this.genericService.isTablet$.pipe(takeUntil(this.isDestroy));
    this.isMobile$ = this.genericService.isMobile$.pipe(takeUntil(this.isDestroy));

    this.router.events.pipe(takeUntil(this.isDestroy)).subscribe((val) => {
      if (val instanceof NavigationEnd && this.isOpen()) {
        this.closeSideBar();
      }
    });
  }

  public ngOnInit(): void {
    this.showMenu = '';
    this.appLayoutService.openSidebarMenu$.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.toggleSidebar();
    });
    this.companiesService.companyChange$.pipe(takeUntil(this.isDestroy)).subscribe((companyChanges) => {
      const previousCompanyId = this.company?.id;
      const nextCompanyId = companyChanges?.current?.id;
      this.company = companyChanges.current;
      if (previousCompanyId === nextCompanyId) {
        return;
      }
      this.menuItemSettings = this.menuConfig;
      this.cdr.markForCheck();
    });

    this.companiesService.userChange$.pipe(takeUntil(this.isDestroy)).subscribe(({ previous, current }) => {
      const prevRoles = (previous?.roles || []).slice().sort();
      const currRoles = (current?.roles || []).slice().sort();
      const rolesChanged = prevRoles.length !== currRoles.length || prevRoles.some((r, i) => r !== currRoles[i]);
      if (!rolesChanged) {
        return;
      }
      this.menuItemSettings = this.menuConfig;
      this.cdr.markForCheck();
    });
    this.applicationModeService.onActiveApplicationModeChange$.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.menuItemSettings = this.menuConfig;
      this.cdr.markForCheck();
    });

    this.genericService.isLaptop$.pipe(distinctUntilChanged(), takeUntil(this.isDestroy)).subscribe((isLaptop: boolean) => {
      this.isDesktop = !isLaptop;
    });
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get hasImportFeature(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.importCustomsFromExcelFeature);
  }

  public isShowBulkDropdown(item: IChildMenuItem): boolean {
    return (
      [enumHeaderActionId.ISF, enumHeaderActionId.ES].includes(item.actionId) && item.dropdown && this.hasImportFeature && this.isDesktop
    );
  }

  public openedMenuItem(menuItem: enumSidebarMenuItem): boolean {
    const localStorageKey = 'MENU_ITEM_OPEN_' + enumSidebarMenuItem[menuItem];
    return !!localStorage.getItem(localStorageKey);
  }

  public toggleMenuItem(menuItem: enumSidebarMenuItem) {
    const localStorageKey = 'MENU_ITEM_OPEN_' + enumSidebarMenuItem[menuItem];
    if (localStorage.getItem(localStorageKey)) {
      localStorage.removeItem(localStorageKey);
    } else {
      localStorage.setItem(localStorageKey, 'true');
    }

    this.cdr.detectChanges();
  }

  public trackByMenuItem(index: number, item: ISidebarMenuItem): string | number {
    const asParent = item as any;
    const asChild = item as any;
    return (asParent && asParent.menuItem) || (asChild && asChild.routerLink) || index;
  }

  public toggleSidebar() {
    if (!this.isOpen()) {
      this.openSideBar();
      return;
    }
  }

  public createNewQuote(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.router) {
      this.router.navigate(['lcl-shipping', 'new']);
    }
  }

  public createNewES(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.router) {
      this.router.navigate(['entry-summary', 'start']);
    }
  }

  public createNewISF(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.router) {
      this.router.navigate(['isf', 'disclaimer']);
    }
  }

  public openSettingSidebar(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.appLayoutService.openSettingSidebar$.next(true);
    this.closeSideBar();
  }

  public switchActiveApplicationMode() {
    this.applicationModeService.switchActiveApplicationMode();
  }

  public onOpenCookieSettings(): void {
    if (!!Cookiebot) {
      Cookiebot.renew();
    }
  }

  public preventDefaultClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public openChildAction(item: IChildMenuItemDropdown): void {
    if (!!item.routerLink) {
      this.router.navigate(item.routerLink);
    } else {
      this[item.action]();
    }
  }

  public get isHasLCL(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.lcl);
  }

  private isOpen(): boolean {
    return this.sideBar && this.sideBar.nativeElement && this.sideBar.nativeElement.classList.contains(this.isOpenSideBarClass);
  }

  private closeSideBar() {
    this.sideBar.nativeElement.classList.remove(this.isOpenSideBarClass);
    this.listener();
    this.openCompanies = false;
  }

  private openSideBar() {
    setTimeout(() => {
      const clickOrTap = typeof document.body.ontouchstart === 'undefined' ? 'click' : 'touchstart';
      this.listener = this.renderer.listen('body', clickOrTap, (event) => {
        if (this.isOpen() && !this.isCurrentTarget(event.target, this.sideBar.nativeElement)) {
          event.stopPropagation();
          this.closeSideBar();
        }
      });
    }, 0);

    this.sideBar.nativeElement.classList.add(this.isOpenSideBarClass);
    // const dom: any = document.querySelector('body');
    // dom.classList.add(this.pushRightClass);
  }

  private isCurrentTarget(el: any, target: any) {
    return el === target ? true : !!el.parentElement ? this.isCurrentTarget(el.parentElement, target) : false;
  }

  private openISFBulkSidebar(): void {
    const componentSidebarRef: ComponentRef<IsfBatchSidebarComponent> = this.appService.openSidebar(IsfBatchSidebarComponent, {
      sidebarHeaderText: 'ISF_BATCH_UPLOAD',
    });
    componentSidebarRef.instance.onCreated.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebar();
    });
  }

  private openEntrySummaryBulkSidebar(): void {
    const componentSidebarRef: ComponentRef<EsBatchSidebarComponent> = this.appService.openSidebar(EsBatchSidebarComponent, {
      sidebarHeaderText: 'ES_BATCH_UPLOAD',
    });
    componentSidebarRef.instance.onCreated.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.appService.closeSidebar();
    });
  }

  private get menuConfig(): ISidebarMenuItem[] {
    const config = [];

    if (this.isActiveUserMode) {
      config.push(...this.userMenuConfig);
    }

    if (this.isActiveSupportMode && !this.isSuperAdmin) {
      config.push(...this.supportMenuConfig);
    }

    if (this.isActiveSupportMode && this.isSuperAdmin) {
      config.push(...this.superAdminMenuConfig);
    }

    return config;
  }

  private get userMenuConfig(): ISidebarMenuItem[] {
    const useConfigChildrenArr = [
      {
        routerLink: '/isf',
        icon: 'icon-ic_isf',
        title: 'ISF',
        plus: {
          routerLink: '/isf/disclaimer',
          tooltip: 'LABELS.NEW_ISF',
          click: this.createNewISF,
        },
        actionId: enumHeaderActionId.ISF,
        dropdown: [
          { title: 'BATCH_UPLOAD', action: 'openISFBulkSidebar' },
          { title: 'SINGLE_ISF_FILLING', routerLink: ['/isf/disclaimer'] },
        ],
      },
      {
        routerLink: '/entry-summary',
        icon: 'icon-ic_es',
        title: 'ENTRY_SUMMARY',
        plus: {
          routerLink: '/entry-summary/start',
          tooltip: 'LABELS.NEW_ES',
          click: this.createNewES,
        },
        actionId: enumHeaderActionId.ES,
        dropdown: [
          { title: 'BATCH_UPLOAD', action: 'openEntrySummaryBulkSidebar' },
          { title: 'SINGLE_ES_FILLING', routerLink: ['/entry-summary/start'] },
        ],
      },
      // TODO uncomment it later
      {
        routerLink: '/bonds',
        title: 'CUSTOMS_BONDS',
      },
      {
        routerLink: '/bina-catalog',
        title: 'BINA_CATALOG',
      },
    ];

    const usConfig: ISidebarMenuItem = {
      menuItem: enumSidebarMenuItem.CUSTOMS,
      title: 'EEZY_CUSTOMS',
      iconUrl: 'icon_doc_green.svg',
      children: useConfigChildrenArr,
    };

    const config: ISidebarMenuItem[] = [
      {
        routerLink: '/dashboard',
        iconUrl: 'icon_home_green.svg',
        title: 'HOME',
      },
    ];

    if (this.isHasLCL) {
      config.push({
        menuItem: enumSidebarMenuItem.LCL,
        title: 'EEZY_FREIGHT',
        iconUrl: 'icon_vessel_green.svg',
        children: [
          {
            routerLink: '/lcl-shipping',
            icon: 'icon-ic_es',
            title: 'SHIPMENTS',
            plus: {
              routerLink: '/lcl-shipping/new',
              tooltip: 'BUTTON.GET_A_QUOTE',
              click: this.createNewQuote,
            },
          },
        ],
      });
    }

    // if (this.isSupport) {
    //   config.push({
    //     routerLink: '/loans',
    //     icon: 'icon-ic_setting',
    //     title: 'LOANS',
    //   });
    // }

    config.push({
      routerLink: '/drip-capital',
      iconUrl: 'icon_drip_menu.svg',
      attachedToTop: true,
      title: 'DRIP',
    });

    //#12171,#12172 - FE should display sidebar as US should see it
    config.splice(1, 0, usConfig);

    return config;
  }

  private get supportDashboardConfig(): ISidebarMenuItem {
    return {
      routerLink: '/dashboard-management',
      icon: 'icon-ic_dashboard',
      title: 'DASHBOARD',
    };
  }

  private get supportCommonMenuConfig(): ISidebarMenuItem[] {
    const accountItems = {
      menuItem: enumSidebarMenuItem.ACCOUNT,
      title: 'ACCOUNT',
      children: [
        {
          routerLink: '/organizations-management',
          icon: 'icon-ic_setting',
          title: 'ORGANIZATIONS',
        },
        {
          routerLink: '/companies-management',
          icon: 'icon-ic_setting',
          title: 'COMPANIES',
        },
        {
          routerLink: '/users-management',
          icon: 'icon-ic_setting',
          title: 'USERS',
        },
        {
          routerLink: '/bills-management',
          icon: 'icon-ic_setting',
          title: 'BILLING',
        },
        {
          routerLink: '/bill-items-management',
          icon: 'icon-ic_setting',
          title: 'BILL_ITEMS',
        },
        {
          routerLink: '/contacts-management',
          icon: 'icon-ic_setting',
          title: 'CONTACTS',
        },
      ],
    };

    if (this.isAdmin) {
      accountItems.children.push(
        {
          routerLink: '/payment-methods-management',
          icon: 'icon-ic_setting',
          title: 'PAYMENT_METHODS',
        },
        {
          routerLink: '/alerts-management',
          icon: 'icon-ic_setting',
          title: 'ALERTS',
        },
      );
    }

    const brokerReviewChild: IChildMenuItem = {
      routerLink: '/customs-broker/entry-summaries',
      icon: 'icon-ic_setting',
      title: 'BROKER_REVIEW',
    };
    const customsChildren: IChildMenuItem[] = this.companiesService.isCustomsBrokerOnly()
      ? [brokerReviewChild]
      : [
          {
            routerLink: '/isfs-management',
            icon: 'icon-ic_setting',
            title: 'ISF_MONITORING',
          },
          {
            routerLink: '/entry-summaries-management',
            icon: 'icon-ic_setting',
            title: 'ES_MONITORING',
          },
          {
            routerLink: '/bonds-management',
            icon: 'icon-ic_setting',
            title: 'BONDS',
          },
          {
            routerLink: '/bond-requests-management',
            icon: 'icon-ic_setting',
            title: 'BOND_REQUESTS',
          },
          {
            routerLink: '/statements-management',
            icon: 'icon-ic_setting',
            title: 'STATEMENTS',
          },
          {
            routerLink: '/lines-management',
            icon: 'icon-ic_setting',
            title: 'LINES',
          },
          {
            routerLink: '/digital-poa-management',
            icon: 'icon-ic_setting',
            title: 'DIGITAL_POA',
          },
          ...(this.companiesService.isCustomsBroker() ? [brokerReviewChild] : []),
        ];

    return [
      accountItems,
      {
        menuItem: enumSidebarMenuItem.CUSTOMS,
        title: 'CUSTOMS',
        children: customsChildren,
      },
      {
        menuItem: enumSidebarMenuItem.LCL,
        title: 'LCL',
        children: [
          {
            routerLink: '/routes-management',
            icon: 'icon-ic_setting',
            title: 'ROUTES',
          },
          {
            routerLink: '/voyages-management',
            icon: 'icon-ic_setting',
            title: 'VOYAGES',
          },
          {
            routerLink: '/containers-management',
            icon: 'icon-ic_setting',
            title: 'CONTAINERS',
          },
          {
            routerLink: '/shipments-management',
            icon: 'icon-ic_setting',
            title: 'SHIPMENTS',
          },
          {
            routerLink: '/quotes-management',
            icon: 'icon-ic_setting',
            title: 'QUOTES',
          },
          {
            routerLink: '/agents-management',
            icon: 'icon-ic_setting',
            title: 'AGENTS',
          },
          {
            routerLink: '/price-list-combined-management',
            icon: 'icon-ic_setting',
            title: 'PRICE_LISTS',
          },
        ],
      },
      {
        menuItem: enumSidebarMenuItem.MARINE_INSURANCE,
        title: 'MARINE_INSURANCE',
        children: [
          {
            routerLink: '/marine-insurance-management',
            icon: 'icon-ic_setting',
            title: 'MARINE_INSURANCE',
          },
        ],
      },
      {
        menuItem: enumSidebarMenuItem.PRODUCTS,
        title: 'PRODUCTS',
        children: [
          {
            routerLink: '/products',
            icon: 'icon-ic_setting',
            title: 'PRODUCTS',
          },
          {
            routerLink: '/product-details',
            icon: 'icon-ic_setting',
            title: 'PRODUCT_DETAILS',
          },
          {
            routerLink: '/brokers-management',
            icon: 'icon-ic_setting',
            title: 'BROKERS',
          },
          {
            routerLink: '/affiliates-management',
            icon: 'icon-ic_setting',
            title: 'AFFILIATES',
          },
          {
            routerLink: '/coupons-management',
            icon: 'icon-ic_setting',
            title: 'COUPONS',
          },
        ],
      },
      {
        routerLink: '/query-bl-management',
        icon: 'icon-ic_setting',
        title: 'QUERY_BL',
      },
      {
        routerLink: '/document-management',
        icon: 'icon-ic_setting',
        title: 'DOCUMENTS',
      },
      {
        routerLink: '/lds-connection-management',
        icon: 'icon-ic_setting',
        title: 'LDS_CONNECTIONS',
      },
    ];
  }

  private get supportMenuConfig(): ISidebarMenuItem[] {
    return [this.supportDashboardConfig, ...this.supportCommonMenuConfig];
  }

  private get superAdminMenuConfig(): ISidebarMenuItem[] {
    return [
      this.supportDashboardConfig,
      {
        menuItem: enumSidebarMenuItem.SETTINGS,
        title: 'SETTING',
        children: [
          {
            routerLink: '/beta-feature-management',
            icon: 'icon-ic_setting',
            title: 'BETA_FEATURES',
          },
          {
            routerLink: '/container-types-management',
            icon: 'icon-ic_setting',
            title: 'CONTAINER_TYPES',
          },
          {
            routerLink: '/distribution-management',
            icon: 'icon-ic_setting',
            title: 'BROKERS_DISTRIBUTION',
          },
          {
            routerLink: '/distribution-group-log',
            icon: 'icon-ic_setting',
            title: 'BROKERS_DISTRIBUTION_LOG',
          },
          {
            routerLink: '/hints-management',
            icon: 'icon-ic_setting',
            title: 'HINTS',
          },
          {
            routerLink: '/sales-taxes-management',
            icon: 'icon-ic_setting',
            title: 'SALES_TAXES',
          },
        ],
      },
      ...this.supportCommonMenuConfig,
    ];
  }
}
